// =============================================================================
// pages/Welcome.js — Phase 74A (post-user-feedback voice pass)
//
// Shown once, right after the essential onboarding, to capture the 6
// most impactful Life Profile answers. Each question lives on its own
// card so the flow feels calm, one-at-a-time. "Skip for now" and
// "Prefer not to say" are always visible so the user is in control.
//
// This page is NOT part of the required onboarding — `profiles.onboarding_complete`
// is already true by the time we get here. It's a soft, dismissable
// deepening step. If the user skips or closes, they can complete it
// later from Settings → Life Profile.
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, Sparkles, SkipForward } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import usePageTitle from '@/hooks/usePageTitle';
import { getMiniFlowQuestions, getValue, setValue, shouldShowQuestion } from '@/lib/lifeProfile';
import { QuestionRow } from '@/pages/SettingsLifeProfile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Welcome() {
  usePageTitle('Help us understand your world');
  const { profile } = useAuth();
  const nav = useNavigate();
  const [step, setStep]  = useState(0);
  const [bag,  setBag]   = useState({});
  const [saving, setSaving] = useState(false);

  // Only ask mini-flow questions that pass their dependency check for
  // this user. E.g. "How predictable is your business revenue?" is
  // hidden for salaried users.
  const questions = useMemo(() => {
    const all = getMiniFlowQuestions();
    return all.filter((q) => shouldShowQuestion(q, { life_profile: bag, profile }));
  }, [bag, profile]);

  const total = questions.length;
  const done = step >= total;
  const current = questions[step];
  const pct = total > 0 ? Math.round((step / total) * 100) : 0;

  // Load existing answers (in case the user is returning after skipping).
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const { data } = await axios.get(`${BACKEND_URL}/api/life-profile`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
          timeout: 10000,
        });
        setBag(data?.life_profile || {});
      } catch (e) {
        // Non-fatal — the page still works, just without pre-fill.
        console.warn('welcome: life-profile prefill failed', e);
      }
    })();
  }, []);

  const persist = async (nextBag) => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.patch(
        `${BACKEND_URL}/api/life-profile`,
        { life_profile: nextBag },
        { headers: { Authorization: `Bearer ${session?.access_token}` }, timeout: 10000 },
      );
    } catch (e) {
      // Non-fatal — user can still proceed; they can edit anytime later.
      console.warn('welcome: save failed', e);
      const status = e?.response?.status;
      if (status === 503) {
        toast.error("We're finishing a quick database update. Your answers will save once it's ready.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onAnswer = (v) => {
    const next = setValue(bag, current.key, v);
    setBag(next);
    persist(next);
  };

  const goNext = () => setStep((s) => s + 1);
  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  const finish = () => {
    toast.success('Thanks — that context will make everything more personal.');
    nav('/dashboard', { replace: true });
  };

  const skipForNow = () => {
    toast('You can complete this anytime from Settings → Life Profile.', {
      icon: <Sparkles className="h-4 w-4 text-primary" />,
    });
    nav('/dashboard', { replace: true });
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full rounded-2xl border-border/60" data-testid="welcome-done-card">
          <CardContent className="p-8 text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-semibold">You&apos;re all set</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We&apos;ll use what you shared to make every answer 2120 gives feel personal to your life.
              You can add or edit anything anytime.
            </p>
            <Button className="rounded-xl w-full" onClick={finish} data-testid="welcome-finish-btn">
              Go to dashboard <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!current) {
    // No mini-flow questions to show at all (edge case). Bounce home.
    return null;
  }

  const currentAnswered = getValue(bag, current.key) !== undefined;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="max-w-xl w-full space-y-6" data-testid="welcome-page">
        {/* Header + progress */}
        <div className="space-y-3 text-center">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold leading-tight" data-testid="welcome-page-title">
            Help us understand your world
          </h1>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="welcome-step-indicator">
            Step {step + 1} of {total}
          </div>
          <Progress value={pct} className="h-1" />
          <p className="text-xs text-muted-foreground leading-relaxed max-w-md mx-auto pt-1">
            The more we understand your life, the more helpful 2120 becomes.
            Answer as much or as little as you&apos;re comfortable sharing.
          </p>
        </div>

        {/* Current question */}
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardContent className="p-6 sm:p-8 space-y-5">
            <QuestionRow
              q={current}
              value={getValue(bag, current.key)}
              onChange={onAnswer}
            />
          </CardContent>
        </Card>

        {/* Nav */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={goPrev}
            disabled={step === 0}
            className="rounded-xl"
            data-testid="welcome-prev-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={skipForNow}
              className="rounded-xl text-muted-foreground"
              data-testid="welcome-skipall-btn"
            >
              <SkipForward className="h-4 w-4 mr-1" /> Skip for now
            </Button>
            <Button
              onClick={goNext}
              className="rounded-xl"
              data-testid="welcome-next-btn"
            >
              {currentAnswered ? 'Continue' : 'Skip this'} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        {saving && (
          <div className="text-[11px] text-muted-foreground text-center" data-testid="welcome-saving">Saving…</div>
        )}
      </div>
    </div>
  );
}
